import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Phone, MapPin, CheckCircle2 } from 'lucide-react';
import { Seo, breadcrumbSchema, faqSchema } from '../components/Seo';
import FaqAccordion from '../components/FaqAccordion';
import QuoteForm from '../components/QuoteForm';
import { SERVICES } from '../data/services';
import { SITE } from '../data/siteConfig';

const SERVICE_LINKS = [
  ...SERVICES.map((s) => ({ path: s.path, nav: s.nav })),
  { path: '/renovation-energetique', nav: 'Rénovation énergétique' },
];

const LocalPage = ({ data }) => {
  const localBusiness = {
    '@context': 'https://schema.org',
    '@type': 'GeneralContractor',
    name: `${SITE.legalName} — ${data.name}`,
    description: data.metaDescription,
    telephone: SITE.phone,
    url: `${SITE.url}${data.path}`,
    image: SITE.logo,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE.street,
      postalCode: SITE.postalCode,
      addressLocality: SITE.city,
      addressRegion: SITE.region,
      addressCountry: 'FR',
    },
    areaServed: { '@type': 'City', name: data.name },
  };
  const breadcrumb = breadcrumbSchema([
    { name: 'Accueil', path: '/' },
    { name: data.name, path: data.path },
  ]);

  return (
    <main className="pt-28" data-testid={`local-${data.slug}`}>
      <Seo
        title={data.metaTitle}
        description={data.metaDescription}
        path={data.path}
        image={data.image}
        jsonLd={[localBusiness, faqSchema(data.faq), breadcrumb]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0">
          <img src={data.image} alt={`Rénovation à ${data.name}`} className="w-full h-full object-cover opacity-25" />
        </div>
        <div className="container mx-auto px-4 relative z-10 py-20 md:py-28">
          <nav className="text-sm text-slate-300 mb-5" aria-label="Fil d’Ariane">
            <Link to="/" className="hover:text-emerald-300">Accueil</Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-slate-200">{data.name}</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full px-4 py-2 text-emerald-200 text-sm font-medium mb-6">
              <MapPin className="h-4 w-4" /> {data.department}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">{data.h1}</h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-8">{data.intro}</p>
            <a
              href="#devis"
              data-testid="local-hero-cta"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:gap-3 shadow-xl"
            >
              Demander un devis gratuit <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Local context */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5">
              Notre intervention à {data.name}
            </h2>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
              {data.context.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services locally */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              Nos prestations à {data.name}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {SERVICE_LINKS.map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  data-testid={`local-service-${s.path}`}
                  className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-5 py-4 hover:border-emerald-500 transition-colors group"
                >
                  <span className="flex items-center gap-3 text-slate-800 group-hover:text-emerald-700">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" /> {s.nav}
                  </span>
                  <ArrowRight className="h-4 w-4 text-emerald-600" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
              Questions fréquentes — {data.name}
            </h2>
            <FaqAccordion items={data.faq} testId={`faq-local-${data.slug}`} />
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="devis" className="py-16 md:py-20 bg-slate-50 scroll-mt-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                  Votre projet à {data.name}
                </h2>
                <p className="text-slate-600">Devis gratuit après étude — joignez vos photos.</p>
              </div>
              <a href={`tel:${SITE.phone}`} className="inline-flex items-center gap-2 text-emerald-700 font-semibold">
                <Phone className="h-5 w-5" /> {SITE.phoneDisplay}
              </a>
            </div>
            <Card className="border-slate-200 shadow-xl bg-white">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900">Demander un devis — {data.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <QuoteForm defaultCity={data.name} />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LocalPage;
